import * as Sentry from "@sentry/browser";
import { Fragment, Node as ProseNode, Slice } from "prosemirror-model";
import { NodeSelection, Plugin } from "prosemirror-state";

import type { AttendeeAttrs } from "~/common/Attendees";

import { Extension } from "../extension";
import { LIST_ITEM_TYPE_NAMES, PLAIN_TEXT_NODE_TYPE_NAMES } from "../nodes/plaintext";
import type { MutableProseNodeAttrs } from "../types";
import { expandSelectionToBlocks } from "../utils";
import { getContext } from "./context";

type JSONContent =
	| { type: "text"; text: string }
	| {
			type: Exclude<string, "text">;
			content?: JSONContent[];
	  };
function isTextNode(node: JSONContent): node is { type: "text"; text: string } {
	return node.type === "text";
}
function removeNewLineFromJSONContent(node: JSONContent) {
	if (isTextNode(node)) {
		while (node.text.endsWith("\n")) {
			node.text = node.text.substring(0, node.text.length - 1);
		}
		node.text = node.text.split("\n").join(" ");
		return;
	}
	if (!node.content) return;
	for (const child of node.content) {
		removeNewLineFromJSONContent(child);
	}
}

/**
 * Sanitizes the contents of paste events.
 *
 * The server will also reject any invalid content, but this provides for a
 * better experience than a cryptic "Integrity Error".
 */
export default class Paste extends Extension {
	rawDetachedDoc: HTMLDocument | null = null;

	get detachedDoc() {
		if (!this.rawDetachedDoc) {
			// Extremely expensive to create, so we keep it around.
			this.rawDetachedDoc = document.implementation.createHTMLDocument("title");
		}
		return this.rawDetachedDoc;
	}

	get name() {
		return "paste";
	}

	get plugins() {
		const extension = this;
		const pastePlugin = new Plugin({
			appendTransaction: (trs, _oldState, newState) => {
				for (const tr of trs) {
					if (tr.getMeta("paste")) {
						const { from, to, $to } = newState.selection;
						const marks = $to.marks();
						const newTr = newState.tr;
						marks.forEach(mark => {
							newTr.removeStoredMark(mark).removeMark(from, to, mark);
						});
						return newTr.setStoredMarks([]);
					}
				}
				return null;
			},
			props: {
				transformPastedHTML(html: string) {
					let userIds: string[] = [];
					if (extension.editorView) {
						const participants = getContext(extension.editorView.state).participants;
						if (participants) {
							userIds = participants.map((u: AttendeeAttrs) => u.id || u.user?.id);
						}
					}

					try {
						const container = extension.detachedDoc.createElement("div");
						container.innerHTML = html;
						container.querySelectorAll("[data-creator], [data-original-block-id], [data-assignees]").forEach(e => {
							// It's cheaper to just try removing it than to
							// check types or presence.
							e.removeAttribute("data-creator");
							e.removeAttribute("data-original-block-id");

							// If at least one of the assignees is not valid for the note
							// the server will reject the update entirely.
							// Strip it out ahead of time.
							const assignees =
								e
									.getAttribute("data-assignees")
									?.split(",")
									.map((assigneeData: string) => {
										const splittedAssigneeData = assigneeData.split(":");
										if (splittedAssigneeData.length < 2) return;
										return { id: splittedAssigneeData[0], status: splittedAssigneeData[1] };
									}) || [];
							const userIdsSet = new Set(userIds);
							if (assignees.some(assignee => assignee && !userIdsSet.has(assignee.id))) {
								e.removeAttribute("data-assignees");
							}
						});
						return container.innerHTML;
					} catch (error) {
						Sentry.captureException(error);
						console.error(error);
						return html;
					}
				},
				transformPasted: originalSlice => {
					// This is to merge single block slices into current selected block
					// And assign new indent attrs to pasted blocks
					const selection = extension.editorView?.state.selection;
					const doc = extension.editorView?.state.doc;
					if (!selection || !doc) return originalSlice;
					let blockSelection: NodeSelection;
					let block: ProseNode | undefined | null = undefined;
					try {
						blockSelection = NodeSelection.create(doc, expandSelectionToBlocks(selection.from, selection.to, doc)[0]);
						block = blockSelection.$anchor.nodeAfter;
					} catch (error) {
						return originalSlice;
					}
					const schema = extension.schema;
					const originalJSON = originalSlice.toJSON();
					if (!schema || !originalJSON) return originalSlice;

					// Trim trailing new lines in node content. This could happen when pasting from certain sources
					// They should've being converted into hard_break node types. Not sure why they aren't
					removeNewLineFromJSONContent(originalJSON as JSONContent);
					let slice = Slice.fromJSON(schema, originalJSON);
					try {
						if (!block) return originalSlice;
						if (slice.content.childCount === 1 && !block.content.size) {
							const child = slice.content.firstChild as ProseNode;
							if (child.isInline || !child.isTextblock) return slice;
							const newContent = block.type.createAndFill(block.attrs, child.content);
							if (!newContent) return slice;
							return new Slice(Fragment.from(newContent), slice.openStart, slice.openEnd);
						} else if (block.attrs.indent > 0) {
							const pastedIsPlainText = (() => {
								for (let index = 0; index < slice.content.childCount; index++) {
									const child = slice.content.child(index);
									if (!PLAIN_TEXT_NODE_TYPE_NAMES.includes(child.type.name)) {
										return false;
									}
								}
								return true;
							})();
							if (
								pastedIsPlainText &&
								block.type.name !== "unstyled" &&
								LIST_ITEM_TYPE_NAMES.includes(block.type.name)
							) {
								const contents: ProseNode[] = [];
								slice.content.forEach(node => {
									if (!block) {
										contents.push(node);
										return;
									}
									const transformedContent = block.type.createAndFill(node.attrs, node.content, node.marks);
									contents.push(transformedContent || node);
								});
								slice = new Slice(Fragment.from(contents), slice.openStart, slice.openEnd);
							}

							// Reindent pasted blocks relative to the destination
							const anchorIndent: number = block.attrs.indent ?? 0;
							let firstIndent: number | null = null;

							for (let i = 0; i < slice.content.childCount; i++) {
								const node = slice.content.child(i);

								// Abort reindentation if we encounter a header since headers are not indented.
								// Everything after the header should not be indented either in order to keep their
								// relative indentation.
								if (node.type.name == "header-one") {
									break;
								}

								const indent: number = node.attrs.indent ?? 0;
								if (firstIndent === null) {
									firstIndent = indent;
								}

								if ("indent" in node.attrs) {
									const relativeIndent = indent - firstIndent;
									(node.attrs as MutableProseNodeAttrs).indent = relativeIndent + anchorIndent;
									if (indent > 6) {
										(node.attrs as MutableProseNodeAttrs).indent = 6;
									}
								}
							}

							return slice;
						}
					} catch (error) {
						Sentry.captureException(error);
						console.error(error);
						return originalSlice;
					}
					return slice;
				},
			},
		});
		return [pastePlugin];
	}
}
